import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Radio,
    Stack,
    IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── tipos ────────────────────────────────────────────────────────────────────

type TipoRespuestaSeguimiento = 1 | 2; // 1=Cumple, 2=No Cumple

export interface SeguimientoRespuesta {
    idPregunta: number;
    idTipoRespuesta: TipoRespuestaSeguimiento;
}

type SeleccionMap = Record<number, TipoRespuestaSeguimiento | null>;

type Pregunta = {
    id: number;
    orden: number;
    numero?: number;
    texto: string;
};

type Seccion = {
    id: number;
    titulo: string;
    preguntas: Pregunta[];
};

interface Props {
    secciones: Seccion[];
    initialSeleccion?: SeleccionMap;
    onChange: (respuestas: SeguimientoRespuesta[], allAnswered: boolean) => void;
}

// ─── fila de pregunta ─────────────────────────────────────────────────────────

interface QuestionRowProps {
    pregunta: Pregunta;
    seleccion: TipoRespuestaSeguimiento | null | undefined;
    onAnswer: (idPregunta: number, idTipoRespuesta: TipoRespuestaSeguimiento) => void;
}

const QuestionRow = React.memo(({ pregunta, seleccion, onAnswer }: QuestionRowProps) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 8fr) minmax(0, 2fr)',
            width: '100%',
            gap: 2,
            alignItems: 'center',
            px: 2,
            py: 1.5,
            border: '1px solid #E0E0E0',
            backgroundColor: 'white'
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{pregunta.numero ?? pregunta.orden}</Typography>
            <Typography>{pregunta.texto}</Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-around">
            {([1, 2] as TipoRespuestaSeguimiento[]).map((opId) => (
                <Radio
                    key={opId}
                    checked={seleccion === opId}
                    onChange={() => onAnswer(pregunta.id, opId)}
                    sx={{
                        p: 0.5,
                        color: '#384A52',
                        '&.Mui-checked': { color: '#384A52' },
                        '& .MuiSvgIcon-root': { fontSize: 26 }
                    }}
                />
            ))}
        </Stack>
    </Box>
));

// ─── componente principal ─────────────────────────────────────────────────────

const SeguimientoVerosimilitudGrid: React.FC<Props> = ({ secciones, initialSeleccion, onChange }) => {
    const [seleccion, setSeleccion] = useState<SeleccionMap>(() => initialSeleccion ?? {});

    const totalPreguntas = useMemo(
        () => secciones.reduce((acc, s) => acc + s.preguntas.length, 0),
        [secciones]
    );

    const notify = useCallback(
        (next: SeleccionMap) => {
            const entries = Object.entries(next);
            const answered = entries.filter(([, v]) => v !== null);
            const allAnswered = answered.length === totalPreguntas;
            const respuestas: SeguimientoRespuesta[] = answered.map(([idStr, v]) => ({
                idPregunta: Number(idStr),
                idTipoRespuesta: v as TipoRespuestaSeguimiento
            }));
            onChange(respuestas, allAnswered);
        },
        [totalPreguntas, onChange]
    );

    // Notifica al parent con el estado inicial para que allAnswered y respuestas sean correctos desde el arranque
    useEffect(() => {
        if (initialSeleccion && Object.keys(initialSeleccion).length > 0) {
            notify(initialSeleccion);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAnswer = useCallback(
        (idPregunta: number, idTipoRespuesta: TipoRespuestaSeguimiento) => {
            setSeleccion((prev) => {
                const next = { ...prev, [idPregunta]: idTipoRespuesta };
                notify(next);
                return next;
            });
        },
        [notify]
    );

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={4}
                mb={1}
                sx={{ backgroundColor: '#384A52', color: 'white' }}
            >
                <Typography color="white" variant="body2" fontWeight="bold">
                    Items a revisar
                </Typography>
            </Box>

            {secciones.map((seccion) => (
                <Accordion
                    key={seccion.id}
                    defaultExpanded
                >
                    <AccordionSummary
                        expandIcon={
                            <IconButton>
                                <ExpandMoreIcon sx={{ color: '#384A52' }} />
                            </IconButton>
                        }
                        sx={{ backgroundColor: '#E0EBED', color: '#384A52' }}
                    >
                        <Typography sx={{ fontWeight: 600 }}>{seccion.titulo}</Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                        {/* Encabezado de columnas */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 8fr) minmax(0, 2fr)',
                                px: 2,
                                py: 1,
                                backgroundColor: '#F6F6F6',
                                border: '1px solid #E0E0E0',
                                color: '#384A52'
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                Condición
                            </Typography>
                            <Stack direction="row" justifyContent="space-around">
                                <Typography variant="body2" fontWeight={600} textAlign="center" sx={{ width: 40 }}>
                                    Cumple
                                </Typography>
                                <Typography variant="body2" fontWeight={600} textAlign="center" sx={{ width: 40 }}>
                                    No Cumple
                                </Typography>
                            </Stack>
                        </Box>

                        {seccion.preguntas.map((pregunta) => (
                            <QuestionRow
                                key={pregunta.id}
                                pregunta={pregunta}
                                seleccion={seleccion[pregunta.id] ?? null}
                                onAnswer={handleAnswer}
                            />
                        ))}
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default SeguimientoVerosimilitudGrid;
